import React, { useState } from 'react';

import { connect } from 'react-redux';

import { buildEmptyProperty } from './helper';
import InsightsSuggestedAddressModal from './suggested_address_modal';
import InsightsSearchModal from './search_modal';
import Modal from 'components/modal';
import { oneLine } from 'helpers/formatters/address_formats';
import { pick } from 'lodash';
import { post } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';

import type { Country } from 'bundles/research/index/types';
import type { Role } from 'bundles/backoffice/contacts/types';
import type { NewProperty, NewPropertyFromInsights, SuggestedProperty } from './types';

export const DEFAULT_CONTACT_ROLE = 'owner_landlord';

// Pre-select the default role (or the only option), matching the add-contact workflow's behaviour.
export function defaultContactRole(roleOptions?: Role[]): Role | null {
  if (!roleOptions?.length) {
    return null;
  }
  return roleOptions.find(option => option.value === DEFAULT_CONTACT_ROLE) ?? roleOptions[0];
}

interface NewPropertyModalProps {
  backdrop?: boolean | 'static';
  backofficeContactId?: number;
  contactRoleOptions?: Role[];
  countryList: Country[];
  // Required with onCreated: the database the new property is owned by.
  databaseId?: number;
  // Seeds the address field each time the modal opens.
  initialAddress?: string;
  newPropertyPath?: string;
  show: boolean;
  onHide: () => void;
  // When given, the property is created in place and handed back rather than redirecting to the
  // new-property page.
  onCreated?: (property: unknown) => void;
}

export function NewPropertyModal({
  backdrop,
  backofficeContactId,
  contactRoleOptions,
  countryList,
  databaseId,
  initialAddress,
  newPropertyPath,
  show,
  onHide,
  onCreated,
}: NewPropertyModalProps) {
  const [searchMessage, setSearchMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [suggestedProperties, setSuggestedProperties] = useState<SuggestedProperty[]>([]);
  const [property, setProperty] = useState(buildEmptyProperty(countryList));
  const [contactRole, setContactRole] = useState<Role | null>(defaultContactRole(contactRoleOptions));

  const changeProperty = (change) => {
    const newProperty = { ...property, ...change };
    setProperty(newProperty);
  };

  const getPropertyParams = (property) => {
    const fields = ['address', 'city'];
    if (property.country?.countryStates?.length) {
      fields.push('state');
    }
    if (property.country?.zipLabel) {
      fields.push('zip');
    }
    return pick(property, fields);
  };

  // The JSON create takes no insights_id, so a picked suggestion is created from its corrected address.
  const createInPlace = async (selection: NewProperty | NewPropertyFromInsights) => {
    let fields = selection as NewProperty;
    if ('insights_id' in selection) {
      const suggestion = suggestedProperties.find(option => option.propertyId === selection.insights_id);
      const country = countryList.find(option => option.isoCode === suggestion?.country) ?? property.country;
      fields = { ...property, ...pick(suggestion, ['address', 'city', 'state', 'zip']), country };
    }

    try {
      const created = await post('/research/properties.json', {
        researchProperty: { ...getPropertyParams(fields), countryId: fields.country.id, databaseId },
      });
      onCreated?.(created);
    } catch (errors) {
      setSearchMessage('');
      setSuggestedProperties([]);
      setError(requestErrors(errors, 'The property could not be created. Try again.').join(' '));
    }
  };

  const createProperty = (property) => {
    if (onCreated) {
      createInPlace(property);
      return;
    }

    let params;
    if (property.insights_id) {
      params = pick(property, ['insights_id']);
    } else {
      params = { ...getPropertyParams(property), country_id: property.country.id };
    }
    if (backofficeContactId) {
      params = {
        ...params,
        backoffice_contact_id: String(backofficeContactId),
        backoffice_contact_role: contactRole?.value ?? DEFAULT_CONTACT_ROLE,
      };
    }
    const urlParams = new URLSearchParams(params);
    window.location.href = `${newPropertyPath}?${urlParams.toString()}`;
  };

  const reset = () => {
    setSearchMessage('');
    setError(undefined);
    setSuggestedProperties([]);
    setProperty({ ...buildEmptyProperty(countryList), address: initialAddress ?? '' });
  };

  const handlePropertySearch = async () => {
    setError(undefined);

    const body = {
      insights_property: getPropertyParams(property),
      only: ['address', 'city', 'country', 'state', 'zip', 'propertyId']
    };
    const insightResponse = await post('/insights_properties/search', body);

    if (insightResponse['total'] === 0) {
      setSearchMessage(`The address ${oneLine(property)} was not found in Insights. Creating new property.`);
      createProperty(property);
    } else {
      setSuggestedProperties(insightResponse['hits']);
    }
  };

  return (
    <Modal backdrop={backdrop} show={show} onHide={onHide} onShow={reset}>
      {suggestedProperties?.length
        ? (
          <InsightsSuggestedAddressModal
            property={property}
            suggestedProperties={suggestedProperties}
            onHide={() => setSuggestedProperties([])}
            onSave={createProperty}
          />
        )
        : (
          <InsightsSearchModal
            contactRole={contactRole}
            contactRoleOptions={contactRoleOptions}
            countryList={countryList}
            error={error}
            info={searchMessage}
            property={property}
            onChange={changeProperty}
            onContactRoleChange={setContactRole}
            onHide={onHide}
            onSearch={handlePropertySearch}
          />
        )}
    </Modal>
  );
}

const mapStateToProps = state => ({
  countryList: state.static.countryList,
});

export default connect(mapStateToProps)(NewPropertyModal);
